import React, { useCallback, useMemo } from "react";

import { InlineOption } from "@/Common/Components/Inputs/InlineOptionsControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FlowsheetConstants } from "@/Flowsheets/FlowsheetConstants";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { CareElement } from "@/Flowsheets/Models/CareElements/CareElement";
import { CareElementType } from "@/Flowsheets/Models/CareElements/CareElementType";
import { TextCareElement } from "@/Flowsheets/Models/CareElements/TextCareElement";
import CareComponentNotes from "./CareComponentParts/CareComponentNotes";
import { CareElementAgentDoseDateReviewed } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementAgentDoseDateReviewed";
import CareElementDateReviewedNumber from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedNumber";
import { CareElementOptionsControl } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedOptionsControl";
import { CareElementDateReviewedRadioOptionsControl } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedRadioOptionsControl";
import { CareElementDateReviewedTextControl } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedTextControl";
import { CareElementYesNoControl } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedYesNoControl";
import { CareElementYesNoWithTextControl } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedYesNoWithTextControl";
import { CareElementFirstLineDateReviewed } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementFirstLineDateReviewed";
import { ReviewedCareElementContainer } from "./CareComponentParts/DateReviewedCareElementComponents/ReviewedDateContainer";
import { ICareComponentProps } from "./Types";

import styles from "./HeartFailureBasicCareComponent.module.scss";

interface HeartFailureBasicCareComponent extends CareComponent {
    CareElements: CareElement[];
}

function renderTitle(title: string): JSX.Element {
    return (
        <div
            className={`${styles.subsectionTitleLabel} ${styles.spanRight} ${styles.subsectionUnderline}`}
        >
            {title}
        </div>
    );
}

const HeartFailureBasicCareComponent = React.memo(
    ({
        isEditable = false,
        state,
        mutateState
    }: ICareComponentProps<HeartFailureBasicCareComponent>): JSX.Element => {
        const { getText } = useTranslation();

        const getLabel = useCallback(
            (elementName: string): string =>
                getText(
                    `Flowsheets_HeartFailureBasicCareComponentLabel_${elementName}`
                ),
            [getText]
        );

        const newYorkHeartAssociationOptions = useMemo(
            () => [
                new InlineOption(
                    FlowsheetConstants.HeartFailureBasic.PhysicalExamination.NewYorkHeartAssociationClassIOptionName,
                    getLabel(
                        FlowsheetConstants.HeartFailureBasic.PhysicalExamination
                            .NewYorkHeartAssociationClassIOptionName
                    )
                ),
                new InlineOption(
                    FlowsheetConstants.HeartFailureBasic.PhysicalExamination.NewYorkHeartAssociationClassIIOptionName,
                    getLabel(
                        FlowsheetConstants.HeartFailureBasic.PhysicalExamination
                            .NewYorkHeartAssociationClassIIOptionName
                    )
                ),
                new InlineOption(
                    FlowsheetConstants.HeartFailureBasic.PhysicalExamination.NewYorkHeartAssociationClassIIIOptionName,
                    getLabel(
                        FlowsheetConstants.HeartFailureBasic.PhysicalExamination
                            .NewYorkHeartAssociationClassIIIOptionName
                    )
                ),
                new InlineOption(
                    FlowsheetConstants.HeartFailureBasic.PhysicalExamination.NewYorkHeartAssociationClassIVOptionName,
                    getLabel(
                        FlowsheetConstants.HeartFailureBasic.PhysicalExamination
                            .NewYorkHeartAssociationClassIVOptionName
                    )
                )
            ],
            [getLabel]
        );

        const educationSelfManagementTrainingOptions = useMemo(() => [
            new InlineOption(
                FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.PatientMedicationUseOptionName,
                getLabel(FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.PatientMedicationUseOptionName)
            ),
            new InlineOption(
                FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.SaltFluidVigilanceOptionName,
                getLabel(FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.SaltFluidVigilanceOptionName)
            ),
            new InlineOption(
                FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.DailyWeightMonitoringOptionName,
                getLabel(FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.DailyWeightMonitoringOptionName)
            ),
            new InlineOption(
                FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.ExerciseActivityOptionName,
                getLabel(FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.ExerciseActivityOptionName)
            )
        ], [getLabel]);

        const targetModifiableRiskFactorsOptions = useMemo(() => [
            new InlineOption(
                FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.HypertensionOptionName,
                getLabel(FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.HypertensionOptionName)
            ),
            new InlineOption(
                FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.DiabetesOptionName,
                getLabel(FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.DiabetesOptionName)
            ),
            new InlineOption(
                FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.HyperlipidemiaOptionName,
                getLabel(FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.HyperlipidemiaOptionName)
            ),
            new InlineOption(
                FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.SmokingOptionName,
                getLabel(FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.SmokingOptionName)
            ),
            new InlineOption(
                FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.OverweightObesityOptionName,
                getLabel(FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.OverweightObesityOptionName)
            )
        ], [getLabel]);

        const replaceCareElement = useCallback(
            (element: CareElement): void => {
                mutateState(s => {
                    s.CareElements = [...s.CareElements];
                    const index = s.CareElements.findIndex(
                        p => p.Name === element.Name
                    );
                    if (index !== -1) {
                        s.CareElements[index] = element;
                    }
                });
            },
            [mutateState]
        );

        const subSectionLookup = useMemo((): Record<string, CareElement[]> => {
            const items =
                state.CareElements?.filter(
                    (CareElement: CareElement) =>
                        !IsNullOrWhiteSpace(CareElement.SubSectionName)
                ) ?? [];

            return items.toLookup(p => p.SubSectionName);
        }, [state.CareElements]);

        const physicalExaminationCareElements = useMemo(
            (): CareElement[] =>
                subSectionLookup[
                    FlowsheetConstants.HeartFailureBasic.PhysicalExamination
                        .SubSectionName
                ] ?? [],
            [subSectionLookup]
        );

        const patientSelfManagementCareElements = useMemo(
            (): CareElement[] =>
                subSectionLookup[
                    FlowsheetConstants.HeartFailureBasic.PatientSelfManagement
                        .SubSectionName
                ] ?? [],
            [subSectionLookup]
        );

        const firstLineCareElements = useMemo(
            (): CareElement[] =>
                subSectionLookup[
                    FlowsheetConstants.HeartFailureBasic.PharmacologicManagementFirstLine
                        .SubSectionName
                ] ?? [],
            [subSectionLookup]
        );

        const symptomReliefCareElements = useMemo(
            (): CareElement[] =>
                subSectionLookup[
                    FlowsheetConstants.HeartFailureBasic.PharmacologicManagementSymptomRelief
                        .SubSectionName
                ] ?? [],
            [subSectionLookup]
        );

        const preventativeCareElements = useMemo(
            (): CareElement[] =>
                subSectionLookup[
                    FlowsheetConstants.HeartFailureBasic.PharmacologicManagementPreventative
                        .SubSectionName
                ] ?? [],
            [subSectionLookup]
        );

        const subsectionRenderContext = useMemo(
            () => ({
                stateId: state.Id,
                isEditable,
                getLabel,
                replaceCareElement
            }),
            [state.Id, isEditable, getLabel, replaceCareElement]
        );

        function renderNotes(element: TextCareElement): JSX.Element {
            const id = `heartFailureBasicCareComponentNotes-${element.Name}`;
            return (
                <CareComponentNotes
                    key={id}
                    id={id}
                    dataCy={id}
                    value={element?.Value ?? ""}
                    isEditable={isEditable}
                    onTextChange={newValue =>
                        replaceCareElement(
                            new TextCareElement({
                                ...element,
                                Value: newValue
                            })
                        )
                    }
                />
            );
        }

        return (
            <div className={styles.HeartFailureCareComponent}>
                {physicalExaminationCareElements.length > 0 && (
                    <>
                        {renderTitle(
                            getText(
                                "Flowsheets_HeartFailureBasicCareComponent_SubSectionName_PhysicalExamination"
                            )
                        )}
                        <div className={styles.careElementsContainer}>
                            {physicalExaminationCareElements.map(
                                careElement => (
                                    <ReviewedCareElementContainer
                                        key={`${careElement.Name}-${state.Id}`}
                                    >
                                        {careElement.CareElementType === CareElementType.IntegerReviewed && (
                                            <CareElementDateReviewedNumber
                                                key={`${careElement.Name}-${state.Id}`}
                                                label={getText(`Flowsheets_HeartFailureBasicCareComponentLabel_${careElement.Name}`)}
                                                isEditable={isEditable}
                                                element={careElement}
                                                onChange={replaceCareElement}
                                                type="integer"
                                                side="column"
                                                unit={getText("Flowsheets_HeartFailureBasicCareComponentLabel_TimesPerWeek")}
                                            />
                                        )}
                                        {careElement.Name ===
                                            FlowsheetConstants.HeartFailureBasic
                                                .PhysicalExamination
                                                .NewYorkHeartAssociationFunctionalCapacityClassificationCareElementName &&
                                            careElement.CareElementType ===
                                                CareElementType.TextReviewed && (
                                                    <CareElementDateReviewedRadioOptionsControl
                                                        label={getText(
                                                            "Flowsheets_HeartFailureBasicCareComponentLabel_NewYorkHeartAssociationFunctionalCapacityClassification"
                                                        )}
                                                        isEditable={isEditable}
                                                        element={careElement}
                                                        onChange={replaceCareElement}
                                                        side="column"
                                                        options={newYorkHeartAssociationOptions}
                                                    />
                                        )
                                        }
                                        {careElement.CareElementType ===
                                            CareElementType.BooleanReviewed && (
                                                <CareElementYesNoControl
                                                    label={getLabel(
                                                        careElement.Name
                                                    )}
                                                    isEditable={isEditable}
                                                    element={careElement}
                                                    onChange={replaceCareElement}
                                                    side="row"
                                                />
                                        )
                                        }
                                        {careElement.CareElementType ===
                                            CareElementType.BooleanTextReviewed && (
                                                <CareElementYesNoWithTextControl
                                                    label={getLabel(
                                                        careElement.Name
                                                    )}
                                                    textLabel={getText(
                                                        "Flowsheets_HeartFailureBasicCareComponentLabel_Location"
                                                    )}
                                                    isEditable={isEditable}
                                                    element={careElement}
                                                    onChange={replaceCareElement}
                                                    side="row"
                                                />
                                        )
                                        }
                                        {careElement.CareElementType ===
                                            CareElementType.Text &&
                                            renderNotes(careElement)
                                        }
                                    </ReviewedCareElementContainer>
                                )
                            )}
                        </div>
                    </>
                )}
                {patientSelfManagementCareElements.length > 0 && (
                    <>
                        {renderTitle(
                            getText(
                                "Flowsheets_HeartFailureBasicCareComponent_SubSectionName_PatientSelfManagement"
                            )
                        )}
                        {patientSelfManagementCareElements.map(careElement => (
                            <ReviewedCareElementContainer key={`${careElement.Name}-${state.Id}`}>
                                {careElement.CareElementType === CareElementType.IntegerReviewed && (
                                    <CareElementDateReviewedNumber
                                        key={`${careElement.Name}-${state.Id}`}
                                        label={getText(`Flowsheets_HeartFailureBasicCareComponentLabel_${careElement.Name}`)}
                                        isEditable={isEditable}
                                        element={careElement}
                                        onChange={replaceCareElement}
                                        type="integer"
                                    />
                                )}
                                {careElement.Name === FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.EducationSelfManagementTrainingCareElementName &&
                                        careElement.CareElementType === CareElementType.MultiSelectReviewed && (
                                            <CareElementOptionsControl
                                                label={getText(
                                                    "Flowsheets_HeartFailureBasicCareComponentLabel_EducationSelfManagementTraining"
                                                )}
                                                isEditable={isEditable}
                                                element={careElement}
                                                onChange={replaceCareElement}
                                                side="column"
                                                options={educationSelfManagementTrainingOptions}
                                            />
                                )
                                }
                                {careElement.Name === FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.TargetModifiableRiskFactorsForHeartFailureAndCoronaryArteryDiseaseCareElementName &&
                                        careElement.CareElementType === CareElementType.MultiSelectReviewed && (
                                            <CareElementOptionsControl
                                                label={getText(
                                                    "Flowsheets_HeartFailureBasicCareComponentLabel_TargetModifiableRiskFactorsForHeartFailureAndCoronaryArteryDisease"
                                                )}
                                                isEditable={isEditable}
                                                element={careElement}
                                                onChange={replaceCareElement}
                                                side="column"
                                                options={targetModifiableRiskFactorsOptions}
                                            />
                                )
                                }
                            </ReviewedCareElementContainer>
                        ))}
                    </>
                )}

                {firstLineCareElements.length > 0 && (
                    <>
                        {renderTitle(
                            getText(
                                "Flowsheets_HeartFailureBasicCareComponent_SubSectionName_PharmacologicManagementFirstLine"
                            )
                        )}
                        <div className={styles.careElementsContainer}>
                            {firstLineCareElements.map(careElement => (
                                careElement.CareElementType === CareElementType.FirstLineReviewed && (
                                    <ReviewedCareElementContainer key={`${careElement.Name}-${state.Id}`}>
                                        <CareElementFirstLineDateReviewed
                                            title={subsectionRenderContext.getLabel(careElement.Name)}
                                            isEditable={subsectionRenderContext.isEditable}
                                            element={careElement}
                                            onChange={subsectionRenderContext.replaceCareElement}
                                        />
                                    </ReviewedCareElementContainer>
                                )))}
                        </div>
                    </>
                )}
                {symptomReliefCareElements.length > 0 && (
                    <>
                        {renderTitle(
                            getText(
                                "Flowsheets_HeartFailureBasicCareComponent_SubSectionName_PharmacologicManagementSymptomRelief"
                            )
                        )}
                        <div className={styles.careElementsContainer}>
                            {symptomReliefCareElements.map(careElement => (
                                <ReviewedCareElementContainer key={`${careElement.Name}-${state.Id}`}>
                                    {careElement.CareElementType === CareElementType.AgentDoseReviewed && (
                                        <CareElementAgentDoseDateReviewed
                                            label={subsectionRenderContext.getLabel(careElement.Name)}
                                            isEditable={subsectionRenderContext.isEditable}
                                            element={careElement}
                                            onChange={subsectionRenderContext.replaceCareElement}
                                            side="column"
                                        />
                                    )}
                                    {careElement.CareElementType === CareElementType.TextReviewed && (
                                        <CareElementDateReviewedTextControl
                                            label={subsectionRenderContext.getLabel(careElement.Name)}
                                            textLabel={getText("Flowsheets_HeartFailureBasicCareComponentLabel_Dose")}
                                            isEditable={subsectionRenderContext.isEditable}
                                            element={careElement}
                                            onChange={subsectionRenderContext.replaceCareElement}
                                            side="column"
                                        />
                                    )}
                                </ReviewedCareElementContainer>
                            ))}
                        </div>
                    </>
                )}
                {preventativeCareElements.length > 0 && (
                    <>
                        {renderTitle(
                            getText(
                                "Flowsheets_HeartFailureBasicCareComponent_SubSectionName_PharmacologicManagementPreventative"
                            )
                        )}
                        <div className={styles.careElementsContainer}>
                            {preventativeCareElements.map(careElement => (
                                <ReviewedCareElementContainer key={`${careElement.Name}-${state.Id}`}>
                                    {careElement.CareElementType === CareElementType.AgentDoseReviewed && (
                                        <CareElementAgentDoseDateReviewed
                                            label={subsectionRenderContext.getLabel(careElement.Name)}
                                            isEditable={subsectionRenderContext.isEditable}
                                            element={careElement}
                                            onChange={subsectionRenderContext.replaceCareElement}
                                            side="column"
                                        />
                                    )}
                                    {careElement.CareElementType === CareElementType.TextReviewed && (
                                        <CareElementDateReviewedTextControl
                                            label={subsectionRenderContext.getLabel(careElement.Name)}
                                            textLabel={getText("Flowsheets_HeartFailureBasicCareComponentLabel_Dose")}
                                            isEditable={subsectionRenderContext.isEditable}
                                            element={careElement}
                                            onChange={subsectionRenderContext.replaceCareElement}
                                            side="column"
                                        />
                                    )}
                                    {careElement.CareElementType === CareElementType.Text &&
                                            renderNotes(careElement)
                                    }
                                </ReviewedCareElementContainer>

                            ))}
                        </div>
                    </>
                )}
            </div>
        );
    }
);

export { HeartFailureBasicCareComponent };
